// Phase 5.2 — sport-profile prompt viewer + editor.
//
// Per-profile modal-less workbench that surfaces all four prompt bundles
// (fkm / verify_keyword / summarize / chapters) with an inline editor
// backed by the sport_profile_prompt_overrides table. Bundled .md files
// stay authoritative in git; the DB row is an overlay resolveProfile
// reads first when present.
//
// UX shape:
//   * Profile picker at the top (all enabled profiles).
//   * Four tabs, one per prompt kind. Active tab is highlighted; overriden
//     tabs get a small MANUAL pill on the tab label so editors can see at
//     a glance which prompts have been edited.
//   * Big textarea holds the *active* content — editable when overriding,
//     showing the bundled bytes otherwise.
//   * "Save Override" writes; "Reset to Bundled" deletes the override row
//     (with a confirm since it discards the editor's work).
//   * A collapsible "Bundled reference" panel below the editor shows the
//     original file bytes as a read-only diff target while the editor
//     works on an override.

import { useState, useEffect, useCallback, useMemo } from 'react'
import { FileText, Save, RotateCcw, ChevronDown, ChevronRight, AlertCircle, Loader2 } from 'lucide-react'
import { useHighlightsApi } from './useHighlightsApi'

const KIND_META = [
  { key: 'fkm',            label: 'Find Key Moments',   hint: 'Main FKM prompt — Claude reads the full transcript and emits candidate moments with grounded cue citations.' },
  { key: 'verify_keyword', label: 'Verify Keyword',      hint: 'Second-pass verifier — Claude re-reads each keyword-sublayer candidate to drop hallucinated / recap / offset hits.' },
  { key: 'summarize',      label: 'Summarize',           hint: 'Post-ingest editorial summary — title + paragraph description + tags.' },
  { key: 'chapters',       label: 'Chapters',            hint: 'Segment the transcript into pre-game / per-period / post-game chapters.' },
]

const KIND_LABELS = Object.fromEntries(KIND_META.map(k => [k.key, k.label]))

export default function PromptsWorkbench({ profiles = [], initialProfileKey = 'hockey' }) {
  const api = useHighlightsApi()
  const [profileKey, setProfileKey] = useState(initialProfileKey)
  const [kind, setKind] = useState('fkm')
  const [data, setData] = useState(null)         // full payload from GET /prompts
  const [draft, setDraft] = useState('')          // textarea value
  const [dirty, setDirty] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const [savedFlash, setSavedFlash] = useState(null)  // 'saved' | 'reset' | null
  const [showBundled, setShowBundled] = useState(false)

  const enabledProfiles = useMemo(
    () => (profiles || []).filter(p => p.is_enabled !== false),
    [profiles],
  )

  const currentPrompt = useMemo(() => {
    if (!data || !Array.isArray(data.prompts)) return null
    return data.prompts.find(p => p.kind === kind) || null
  }, [data, kind])

  // Load prompt payload when profile changes.
  const refresh = useCallback(async (targetProfile = profileKey) => {
    setBusy(true); setErr(null)
    try {
      const res = await api.getProfilePrompts(targetProfile)
      setData(res)
      // Reset the draft to the freshly-loaded active content for the current kind.
      const p = (res?.prompts || []).find(x => x.kind === kind)
      setDraft(p?.active || '')
      setDirty(false)
      setSavedFlash(null)
    } catch (e) {
      setErr(e.message || String(e))
    } finally {
      setBusy(false)
    }
  }, [api, profileKey, kind])

  useEffect(() => { refresh(profileKey) }, [profileKey])  // eslint-disable-line react-hooks/exhaustive-deps

  // Kind switch: reset draft to whichever active content that kind carries.
  useEffect(() => {
    if (!data) return
    const p = data.prompts.find(x => x.kind === kind)
    setDraft(p?.active || '')
    setDirty(false)
    setSavedFlash(null)
    setShowBundled(false)
  }, [kind, data])

  const onDraftChange = (v) => {
    setDraft(v)
    const p = currentPrompt
    // Dirty relative to whichever the user is currently editing. If they
    // typed something that matches the ACTIVE bytes exactly (i.e. bundled
    // when no override, or the existing override), consider clean.
    setDirty(p ? v !== p.active : v.length > 0)
    setSavedFlash(null)
  }

  const canSave = dirty && draft.trim().length > 0 && !busy
  const canReset = !!currentPrompt?.is_overridden && !busy

  const onSave = async () => {
    if (!canSave) return
    setBusy(true); setErr(null)
    try {
      await api.patchProfilePrompt(profileKey, kind, draft)
      await refresh(profileKey)
      setSavedFlash('saved')
    } catch (e) {
      setErr(e.message || String(e))
    } finally {
      setBusy(false)
    }
  }

  const onReset = async () => {
    if (!canReset) return
    if (!window.confirm(
      `Reset "${KIND_LABELS[kind]}" for profile "${profileKey}" back to the bundled .md file?\n\n` +
      `The override in the database will be deleted. You'll lose your current edit.`
    )) return
    setBusy(true); setErr(null)
    try {
      await api.deleteProfilePrompt(profileKey, kind)
      await refresh(profileKey)
      setSavedFlash('reset')
    } catch (e) {
      setErr(e.message || String(e))
    } finally {
      setBusy(false)
    }
  }

  // Header + profile picker
  const overridesByKind = useMemo(() => {
    const m = new Map()
    for (const p of (data?.prompts || [])) m.set(p.kind, p.is_overridden)
    return m
  }, [data])

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        padding: '10px 14px', background: '#161616', border: '1px solid #333', borderRadius: 6,
      }}>
        <FileText size={16} color="#3b82f6" />
        <div style={{ fontWeight: 600, fontSize: 14 }}>Prompt Workbench</div>
        <span style={{ fontSize: 11, color: '#888' }}>
          Edit the LLM prompt content per sport profile. Overrides live in D1 and take effect on the next FKM run (5-min registry cache).
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 11, color: '#888' }}>Profile:</label>
          <select
            value={profileKey}
            onChange={e => setProfileKey(e.target.value)}
            disabled={busy}
            style={{
              background: '#222', color: '#ddd', border: '1px solid #333',
              borderRadius: 4, padding: '4px 8px', fontSize: 12, minWidth: 140,
            }}>
            {enabledProfiles.length === 0 && (
              <option value={initialProfileKey}>{initialProfileKey}</option>
            )}
            {enabledProfiles.map(p => (
              <option key={p.profile_key} value={p.profile_key}>{p.display_name}</option>
            ))}
          </select>
        </div>
      </div>

      {err && (
        <div style={{
          padding: 10, background: 'rgba(220,38,38,0.08)', border: '1px solid #4a1414',
          borderRadius: 4, color: '#fca5a5', fontSize: 12,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <AlertCircle size={14} /> {err}
        </div>
      )}

      {/* Kind tabs */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #333' }}>
        {KIND_META.map(k => {
          const active = k.key === kind
          const overridden = overridesByKind.get(k.key)
          return (
            <button
              key={k.key}
              onClick={() => setKind(k.key)}
              title={k.hint}
              style={{
                padding: '8px 14px',
                background: active ? '#0e0e0e' : 'transparent',
                border: 'none',
                borderBottom: active ? '2px solid #3b82f6' : '2px solid transparent',
                marginBottom: -1,
                color: active ? '#fff' : '#888',
                fontWeight: active ? 600 : 500,
                fontSize: 13,
                cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}>
              {k.label}
              {overridden && (
                <span style={{
                  fontSize: 9, padding: '1px 5px', borderRadius: 8,
                  background: '#1e3a8a', color: '#bfdbfe', fontWeight: 600,
                  letterSpacing: 0.4,
                }}>MANUAL</span>
              )}
            </button>
          )
        })}
      </div>

      {/* Status line for the currently-selected kind */}
      {currentPrompt && (
        <div style={{
          padding: '8px 12px', background: '#0e0e0e', border: '1px solid #222',
          borderRadius: 4, fontSize: 11, color: '#888',
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        }}>
          {currentPrompt.is_overridden ? (
            <>
              <span style={{
                padding: '2px 8px', borderRadius: 10,
                background: '#1e3a8a', color: '#bfdbfe', fontWeight: 600,
              }}>MANUAL OVERRIDE</span>
              <span>
                Updated {currentPrompt.updated_at || '?'} by {currentPrompt.updated_by || '?'}
              </span>
            </>
          ) : (
            <>
              <span style={{
                padding: '2px 8px', borderRadius: 10,
                background: '#333', color: '#bbb', fontWeight: 600,
              }}>BUNDLED</span>
              <span>Using the .md file from git — no override in D1.</span>
            </>
          )}
          <span style={{ marginLeft: 'auto' }}>
            {draft.length.toLocaleString()} chars / {new TextEncoder().encode(draft).length.toLocaleString()} bytes
            <span style={{ color: '#555' }}> · 32 KB cap</span>
          </span>
        </div>
      )}

      {/* Editable content */}
      {busy && !data ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>
          <Loader2 size={14} className="spin" style={{ verticalAlign: 'middle', marginRight: 6 }} />
          Loading prompts…
        </div>
      ) : currentPrompt ? (
        <>
          <textarea
            value={draft}
            onChange={e => onDraftChange(e.target.value)}
            spellCheck={false}
            style={{
              width: '100%', minHeight: 420,
              background: '#0e0e0e', color: '#e5e5e5',
              border: '1px solid ' + (dirty ? '#f59e0b' : '#333'),
              borderRadius: 4, padding: 12,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
              fontSize: 12, lineHeight: 1.5, resize: 'vertical',
              boxSizing: 'border-box',
            }}
          />

          {/* Action bar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={onSave} disabled={!canSave}
              title={dirty ? 'Store this content as an override' : 'No changes to save'}
              style={{
                padding: '6px 14px',
                background: canSave ? '#22c55e' : '#222',
                border: 'none', borderRadius: 4,
                color: canSave ? '#fff' : '#666',
                fontWeight: 600, fontSize: 12,
                cursor: canSave ? 'pointer' : 'default',
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}>
              {busy ? <Loader2 size={13} className="spin" /> : <Save size={13} />}
              Save Override
            </button>
            <button onClick={onReset} disabled={!canReset}
              title={canReset ? 'Delete the override; revert to bundled .md file' : 'No override set for this kind'}
              style={{
                padding: '6px 14px',
                background: 'transparent',
                border: '1px solid ' + (canReset ? '#dc2626' : '#333'),
                borderRadius: 4,
                color: canReset ? '#fca5a5' : '#555',
                fontSize: 12,
                cursor: canReset ? 'pointer' : 'default',
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}>
              <RotateCcw size={12} /> Reset to Bundled
            </button>
            {savedFlash && (
              <span style={{ fontSize: 11, color: '#22c55e' }}>
                {savedFlash === 'saved' ? '✓ Override saved. New FKM runs will pick it up within 5 min.' : '✓ Override cleared. Bundled version is now active.'}
              </span>
            )}
            {dirty && !savedFlash && (
              <span style={{ fontSize: 11, color: '#f59e0b' }}>Unsaved changes</span>
            )}
          </div>

          {/* Bundled reference collapsible — only meaningful when overridden */}
          <div style={{ marginTop: 4 }}>
            <button onClick={() => setShowBundled(v => !v)}
              style={{
                background: 'transparent', border: 'none', color: '#888',
                cursor: 'pointer', padding: '4px 0', fontSize: 11,
                display: 'inline-flex', alignItems: 'center', gap: 4,
              }}>
              {showBundled ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              {currentPrompt.is_overridden
                ? 'Show bundled version (git .md file — read-only reference)'
                : 'Show bundled version (same as active — you\'re looking at it above)'}
            </button>
            {showBundled && (
              <pre style={{
                margin: '6px 0 0 0', padding: 12,
                background: '#0e0e0e', border: '1px dashed #333', borderRadius: 4,
                color: '#aaa', fontSize: 12, lineHeight: 1.5,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                maxHeight: 360, overflow: 'auto',
              }}>{currentPrompt.bundled}</pre>
            )}
          </div>
        </>
      ) : (
        <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>
          No prompt data for this profile.
        </div>
      )}
    </div>
  )
}
